/**
 * CorretivaPage — Kanban admin pra workflow Manutenção/Corretiva.
 *
 * BUG-B1.6.2 (2026-05-28). Substitui o ManutencaoKanban tradicional
 * APENAS pro slug 'corretiva' — Preventiva/Vistoria continuam usando
 * service_orders via ManutencaoKanban (zero regressão).
 *
 * Diferenças do ManutencaoKanban tradicional:
 *   - Lê de `deals` (workflow_slug='corretiva') em vez de `service_orders`
 *   - 4 colunas (Abertas / Agendadas / Canceladas / Concluídas) — migration 145
 *   - Click no card abre ChamadoDealDetailDialog (modal próprio, mais leve
 *     que DealDetailDialog do Comercial — sem orçamento/produto/serviço)
 *   - "Atribuir técnico" cria service_order linkada + move pra Agendadas
 *   - Cards "Concluída" ocultos por default (toggle "Mostrar finalizados")
 *
 * NÃO REVERTER:
 *   - workflowSlug='corretiva' (não 'manutencao-corretiva') — alinhado
 *     com chamados.ts WORKFLOW_CHAMADO + kanban_custom_columns DB
 *   - Branch em ManutencaoPage isola: só corretiva entra aqui
 *   - includeInactive controlado pelo toggle "Mostrar concluídos" — admin
 *     vê histórico arquivado quando quer
 *   - DnD manual entre colunas ainda funciona via PUT /deals/:id/stage —
 *     útil pra admin cancelar / pular pra concluida sem técnico (raros mas
 *     possíveis casos)
 */

import { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useSearchParams } from 'react-router-dom';
// FASE-C (design system): emoji-ícone banido — Lucide only.
import { Settings2 } from 'lucide-react';

import {
  Badge,
  Button,
  PageHeader,
  Switch,
  useToast,
} from '@/components/ui';
import { KanbanBoard, type KanbanCardProps } from '@/components/kanban/KanbanBoard';
import { useAuth } from '@/contexts/AuthContext';
import { useDeals, useDealsQuery, useDealActions, useDeal } from '@/lib/comercial-store';
import { useClientNome } from '@/lib/clients-store';
import { useKanbanColumns, type DefaultStage } from '@/lib/use-kanban-columns';
import { useAutomations } from '@/lib/automations-store';
import { AutomationsPanel } from '@/components/automations';
import { cn } from '@/lib/cn';
import type { Deal } from '@/lib/comercial-types';

import { ChamadoDealDetailDialog } from './ChamadoDealDetailDialog';

import { formatarData } from '@/lib/format-date';
/** Defaults espelhando migration 145 (slugs SINGULAR, labels PLURAIS). */
const CORRETIVA_DEFAULTS: DefaultStage[] = [
  { slug: 'aberta', label: 'Abertas', ordem: 1, variant: 'warning' },
  { slug: 'agendada', label: 'Agendadas', ordem: 2, variant: 'info' },
  { slug: 'cancelada', label: 'Canceladas', ordem: 3, variant: 'danger' },
  { slug: 'concluida', label: 'Concluídas', ordem: 4, variant: 'success' },
];

/**
 * SLA do chamado Corretiva (FEAT-B1.6.4).
 *
 * Limites (calibrados na ausência de configuração por cliente — admin pode
 * customizar em rodada futura via tabela sla_policy):
 *   - fase=aberta (aguardando triagem): ≤24h ok, 24-48h warning, >48h overdue
 *   - fase=agendada: ≤48h ok, 48-168h (7d) warning, >168h overdue
 *   - fase=cancelada/concluida: neutro (sem SLA aplicável)
 *
 * Retorna shape compatível com `getItemHealth` do KanbanBoard:
 *   - 'healthy' = verde, sem dor
 *   - 'idle'    = amarelo (atenção)
 *   - 'overdue' = vermelho (urgente)
 */
function getChamadoHealth(deal: Deal): 'healthy' | 'idle' | 'overdue' | null {
  if (deal.fase === 'cancelada' || deal.fase === 'concluida') return null;
  const opened = new Date(deal.criadoEmIso).getTime();
  if (Number.isNaN(opened)) return null;
  const horas = (Date.now() - opened) / 3_600_000;
  if (deal.fase === 'aberta') {
    if (horas < 24) return 'healthy';
    if (horas < 48) return 'idle';
    return 'overdue';
  }
  // agendada (ou outras fases ativas)
  if (horas < 48) return 'healthy';
  if (horas < 168) return 'idle';
  return 'overdue';
}

/** Dias parado (helper pra exibir no card). */
function getDiasParado(deal: Deal): number {
  const opened = new Date(deal.criadoEmIso).getTime();
  if (Number.isNaN(opened)) return 0;
  return Math.floor((Date.now() - opened) / 86_400_000);
}

interface CorretivaPageProps {
  workflow: {
    id: string;
    slug: string;
    nome: string;
    cor: string | null;
    icone: string | null;
    responsavel?: { id: string; nome: string; fotoUrl: string | null } | null;
  };
}

// Context pra cards receberem onView/onAtribuir sem prop-drill
interface CardCtxValue {
  onView: (deal: Deal) => void;
}
const CardContext = createContext<CardCtxValue | null>(null);
function useCardContext() {
  const ctx = useContext(CardContext);
  if (!ctx) throw new Error('CardContext missing');
  return ctx;
}

export function CorretivaPage({ workflow }: CorretivaPageProps) {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;
  const [mostrarConcluidos, setMostrarConcluidos] = useState(false);
  const [automationsPanelOpen, setAutomationsPanelOpen] = useState(false);

  const deals = useDeals({ workflow: 'corretiva', includeInactive: mostrarConcluidos });
  const { isLoading: dealsLoading, isError: dealsError } = useDealsQuery({
    workflow: 'corretiva',
    includeInactive: mostrarConcluidos,
  });
  const { changeStage } = useDealActions();
  const { show: showToast } = useToast();

  // Automações (admin only) — mesma engine do Comercial.
  const automations = useAutomations('corretiva');
  const automationsAtivasCount = automations.filter((a) => a.enabled).length;
  const { columns: kanbanColumns, getLabel: getColumnLabel } = useKanbanColumns(
    'corretiva',
    CORRETIVA_DEFAULTS,
  );

  const [detailing, setDetailing] = useState<Deal | null>(null);

  // Deep-link ?openCard=:dealId — admin abre direto pelo link da notif
  const [searchParams, setSearchParams] = useSearchParams();
  const openCardId = searchParams.get('openCard') ?? searchParams.get('openDeal') ?? null;
  const deeplinkDeal = useDeal(openCardId ?? undefined);

  useEffect(() => {
    if (!openCardId) return;
    if (deeplinkDeal) {
      setDetailing(deeplinkDeal);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCardId, deeplinkDeal]);

  const cardCtx = useMemo<CardCtxValue>(
    () => ({ onView: (d) => setDetailing(d) }),
    [],
  );

  if (dealsLoading && deals.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-ink-3 dark:text-ink-dark-3">
        Carregando chamados…
      </div>
    );
  }
  if (dealsError) {
    return (
      <div className="py-12 text-center text-sm text-danger">
        Erro ao carregar chamados. Tente recarregar a página.
      </div>
    );
  }

  return (
    <CardContext.Provider value={cardCtx}>
      <div data-testid="corretiva-page">
        {/* FASE-C (design system): PageHeader canônico — mesmo padrão da
            Preventiva; ações quebram linha no 375px sem estourar. */}
        <PageHeader
          crumb="Manutenção"
          title={workflow.nome}
          subtitle="Chamados abertos pelos clientes no Portal e pela equipe IGM."
          className="mb-4"
          actions={
            <>
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAutomationsPanelOpen(true)}
                  data-testid="corretiva-automations-button"
                >
                  <Settings2 className="w-4 h-4 mr-1.5" aria-hidden="true" />
                  Automações ({automationsAtivasCount} ativa
                  {automationsAtivasCount === 1 ? '' : 's'})
                </Button>
              )}
              <Switch
                checked={mostrarConcluidos}
                onChange={(e) => setMostrarConcluidos(e.target.checked)}
                label="Mostrar finalizados"
                data-testid="corretiva-toggle-concluidos"
              />
            </>
          }
        />

        <KanbanBoard<Deal>
          workflowSlug="corretiva"
          defaultStages={CORRETIVA_DEFAULTS}
          items={deals}
          getItemStage={(d) => d.fase}
          cardComponent={ChamadoCard}
          onStageChange={async ({ item, toSlug, fromLabel, toLabel }) => {
            if (!user) return;
            try {
              await changeStage(item.id, toSlug, user.id, user.name, {
                from: fromLabel,
                to: toLabel,
              });
              showToast(`Card movido pra "${toLabel}".`, { variant: 'success' });
            } catch (err) {
              showToast(`Erro ao mover: ${(err as Error).message}`, { variant: 'error' });
            }
          }}
          isAdmin={isAdmin}
          // FEAT-B1.6.4: SLA — KanbanBoard mostra badges agregados (overdue/idle)
          // por coluna e card consome via prop. ChamadoCard renderiza badge inline.
          getItemHealth={getChamadoHealth}
          emptyState={{
            title: 'Nenhum chamado',
            description:
              'Quando um cliente abrir chamado pelo Portal ou um colaborador criar deal aqui, vai aparecer.',
          }}
          emptyColumnText="Sem chamados nesta fase"
        />

        {detailing && (
          <ChamadoDealDetailDialog
            open={!!detailing}
            deal={detailing}
            onClose={() => setDetailing(null)}
          />
        )}

        {/* Painel Automações (admin) — mesma engine/UI do Comercial.
            Suporta triggers por coluna (deal_entered/left) e por tag,
            actions cross-workflow (Fase B), automações ativas/inativas. */}
        {isAdmin && (
          <AutomationsPanel
            open={automationsPanelOpen}
            onClose={() => setAutomationsPanelOpen(false)}
            workflowSlug="corretiva"
            deals={deals}
            columns={kanbanColumns}
            getColumnLabel={getColumnLabel}
          />
        )}
      </div>
    </CardContext.Provider>
  );
}

// ─── ChamadoCard ───────────────────────────────────────────────────────

function ChamadoCard({ item: deal, healthFilter }: KanbanCardProps<Deal>) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: deal.id,
  });
  const clienteNome = useClientNome(deal.clienteId);
  const ctx = useCardContext();

  const isInativo = deal.ativo === false;
  const health = getChamadoHealth(deal);
  const dias = getDiasParado(deal);
  const dimmed = healthFilter !== null && health !== healthFilter;
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 10 }
    : undefined;

  // Data abreviada pt-BR
  const dataAbreviada = (() => {
    try {
      return formatarData(deal.criadoEmIso, {
        day: '2-digit',
        month: '2-digit',
      });
    } catch {
      return '';
    }
  })();

  // FEAT-B1.6.4: SLA badge — texto curto + variant que reflete getChamadoHealth.
  // Mostra "Xd" (dias) ou "hoje" pra <24h. Em cancelada/concluida, badge oculto.
  const slaLabel = (() => {
    if (health === null) return null; // cancelada / concluida
    if (dias === 0) return 'hoje';
    return `${dias}d`;
  })();
  const slaVariant: 'success' | 'warning' | 'danger' | 'neutral' =
    health === 'overdue' ? 'danger' : health === 'idle' ? 'warning' : health === 'healthy' ? 'success' : 'neutral';

  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-card-${deal.id}`}
      data-inativo={isInativo || undefined}
      data-health={health ?? undefined}
      data-dimmed={dimmed || undefined}
      style={style}
      className={cn(
        'group relative bg-paper-surface dark:bg-paper-dark-surface rounded-lg border border-line dark:border-line-dark p-3 cursor-grab active:cursor-grabbing transition-shadow',
        isDragging ? 'opacity-50 shadow-lg' : 'hover:shadow-sm',
        isInativo && 'opacity-60',
        // SLA emphasis: cards atrasados ganham borda esquerda vermelha
        health === 'overdue' && !isInativo && 'border-l-4 border-l-red',
        dimmed && 'opacity-30',
      )}
      {...attributes}
      {...listeners}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          ctx.onView(deal);
        }}
        className="absolute inset-0 z-[1] cursor-pointer"
        aria-label={`Abrir chamado ${deal.titulo}`}
      />
      <div className="relative pointer-events-none">
        {deal.humanLabel && (
          <p className="text-xs font-mono text-ink-3 dark:text-ink-dark-3">
            {deal.humanLabel}
          </p>
        )}
        <p className="font-semibold text-sm text-ink-1 dark:text-ink-dark-1 leading-tight truncate mt-0.5">
          {deal.titulo}
        </p>
        <p className="text-xs text-ink-2 dark:text-ink-dark-2 mt-1 truncate">
          {clienteNome || deal.clienteId}
        </p>
        <div className="flex items-center justify-between mt-2 gap-2">
          <span className="text-xs text-ink-3 dark:text-ink-dark-3">{dataAbreviada}</span>
          <div className="flex items-center gap-1.5">
            {deal.responsavelNome && (
              <Badge variant="info" size="sm">
                {deal.responsavelNome.split(' ')[0]}
              </Badge>
            )}
            {slaLabel && (
              <Badge
                variant={slaVariant}
                size="sm"
                dot={health === 'overdue' || health === 'idle'}
                title={`SLA: ${dias === 0 ? 'aberto há menos de 24h' : `parado há ${dias} dia(s)`}`}
              >
                {slaLabel}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
